/**
 * FRE folder-classification + list-transform logic (A2F-993/994/1035).
 *
 * Pure, React-free helpers extracted from `FrePage.tsx` so the load-bearing
 * Disable/Enable + classifier + reorder logic is unit-testable. The page wires
 * these into `useMemo`/handlers; behavior must stay identical.
 *
 * The matching model is the case-insensitive folder-name glob from
 * `lib/glob.ts` (which mirrors the backend's `fnmatch` semantics), so "what the
 * FRE discovery list marks as ignored = what ingest skips server-side".
 */
import { globMatchAny } from '@/lib/glob';

/**
 * One of three render/lifecycle states for a discovered top-level folder,
 * mirroring Manage Project's "Show hidden" view:
 *   - active   → not matched by any effective ignore glob. Numbered, draggable,
 *                with a Disable action.
 *   - disabled → excluded AND removing the exact-name (casefolded) ignore chip
 *                would un-exclude it. The Manage-Project `is_disabled` analog:
 *                "Disabled" badge + reversible Enable. Covers both a click of
 *                Disable and an exact seeded default (e.g. `archive`).
 *   - rule     → excluded by a wildcard pattern (`*pec*`) or system core
 *                (`.*` / `~$*`), i.e. removing the exact-name chips would NOT
 *                un-exclude it. The "Ignored by rule" analog: badge, no toggle.
 */
export type FolderState = 'active' | 'disabled' | 'rule';

/** Minimal shape the classifier needs — anything carrying a folder name. */
export interface NamedFolder {
  folderName: string;
}

export interface AnnotatedFolder<F extends NamedFolder> {
  folder: F;
  state: FolderState;
}

/**
 * Classify each discovered folder against the effective ignore-glob set
 * (systemCore ∪ the user's current ignore chips).
 *
 * The subtle branch: a folder excluded by BOTH its exact name AND a wildcard
 * stays `rule` — removing the exact-name chip wouldn't un-exclude it, so Enable
 * is withheld (it would be a no-op).
 */
export function annotateFolders<F extends NamedFolder>(
  folders: readonly F[],
  effectiveIgnoreGlobs: readonly string[],
): AnnotatedFolder<F>[] {
  return folders.map((folder) => {
    const excluded = globMatchAny(folder.folderName, effectiveIgnoreGlobs);
    if (!excluded) return { folder, state: 'active' as FolderState };
    // Enable-able iff removing every exact-name ignore chip would stop it
    // matching the effective set (so wildcard/system-core matches stay locked).
    const remaining = effectiveIgnoreGlobs.filter(
      (g) => g.toLowerCase() !== folder.folderName.toLowerCase(),
    );
    const enableable = !globMatchAny(folder.folderName, remaining);
    return { folder, state: (enableable ? 'disabled' : 'rule') as FolderState };
  });
}

/**
 * Disable a folder: add its EXACT name to the ignore list (case-insensitive
 * dedupe, matching the backend). Returns the input array unchanged when an
 * equal-casefolded chip is already present, so it's safe to feed into a React
 * setter without spurious re-renders.
 */
export function addIgnoreName(
  ignoreFolders: readonly string[],
  folderName: string,
): string[] {
  return ignoreFolders.some((g) => g.toLowerCase() === folderName.toLowerCase())
    ? [...ignoreFolders]
    : [...ignoreFolders, folderName];
}

/**
 * Enable a folder: remove every ignore chip whose casefolded value equals the
 * folder's casefolded name (the exact-name chip that disabled it). System-core
 * entries and wildcard patterns are untouched — which is why only `disabled`
 * rows expose Enable.
 */
export function removeIgnoreName(
  ignoreFolders: readonly string[],
  folderName: string,
): string[] {
  return ignoreFolders.filter((g) => g.toLowerCase() !== folderName.toLowerCase());
}

/**
 * Stable-merge reorder for the active subset.
 *
 * `nextActive` is the active rows in their new order; this walks the full
 * discovery order and, for each slot, takes the next active row (in its new
 * order) if that slot was active, else keeps the excluded row in place. Excluded
 * (rule/disabled) rows therefore stay pinned at their original positions, and
 * the result is always the complete set in the same length/order skeleton.
 *
 * Precondition: `nextActive` must be exactly the active subset of `full` under
 * `effectiveIgnoreGlobs` (the same predicate `annotateFolders` uses), in the
 * desired new order — i.e. `nextActive.length === full.filter(not-excluded).length`.
 * The sole caller (FrePage) derives it that way; a shorter `nextActive` would
 * splice `undefined` into the result.
 */
export function mergeActiveOrder<F extends NamedFolder>(
  full: readonly F[],
  nextActive: readonly F[],
  effectiveIgnoreGlobs: readonly string[],
): F[] {
  let a = 0;
  return full.map((f) =>
    globMatchAny(f.folderName, effectiveIgnoreGlobs) ? f : nextActive[a++],
  );
}
